import React, { useId, useState } from "react";
import {
  Box,
  Button,
  ExpandableSection,
  SpaceBetween,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  OmsExportPreset,
  OmsPresetScope,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { OmsConfiguration } from "./omsPresetEditing";
import presetChanges, {
  PresetChange,
  PresetChangeValue,
} from "./omsPresetChanges";
import { comparisonLabel } from "./omsReportFormatting";

function PresetChangeCell({
  value,
  report,
}: {
  value: PresetChangeValue;
  report: OmsReport;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const { filter } = value;
  const text = filter
    ? comparisonLabel(filter.operator, report.columnTypes?.[filter.column])
    : value.text;
  const values = filter
    ? filter.values || (filter.value !== undefined ? [filter.value] : [])
    : value.values || [];
  const labels = values.map((item) => (item === "" ? '"" (blank)' : item));
  let unit = "column";
  if (filter) unit = "value";
  else if (value.kind === "filters") unit = "filter";
  if (labels.length > 3 || labels.join(", ").length > 160)
    return (
      <SpaceBetween size="xs">
        {text && <span>{text}</span>}
        <Button
          ariaExpanded={expanded}
          ariaControls={detailsId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Hide" : "Show"} {labels.length}{" "}
          {labels.length === 1 ? unit : `${unit}s`}
        </Button>
        <div id={detailsId} hidden={!expanded}>
          {expanded && (
            <ol className="oms-report-download__applied-filters">
              {labels.map((item, index) => (
                // Duplicate values are possible in an ordered filter-field list.
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{item}</li>
              ))}
            </ol>
          )}
        </div>
      </SpaceBetween>
    );
  return (
    <span>
      {text}
      {text && labels.length > 0 && " "}
      {labels.join(", ")}
    </span>
  );
}

function changeColumns(
  report: OmsReport,
  saved: OmsExportPreset
): TableProps.ColumnDefinition<PresetChange>[] {
  const savedReport = { ...report, columnTypes: saved.schemaTypes };
  return [
    {
      id: "setting",
      header: "Setting",
      width: 180,
      cell: (change) => change.label,
    },
    {
      id: "before",
      header: "Saved",
      cell: (change) => (
        <PresetChangeCell value={change.before} report={savedReport} />
      ),
    },
    {
      id: "after",
      header: "Updated",
      cell: (change) => (
        <PresetChangeCell value={change.after} report={report} />
      ),
    },
  ];
}

export default function OMSPresetChanges({
  saved,
  draft,
  name,
  scope,
  report,
}: {
  saved: OmsExportPreset;
  draft: OmsConfiguration;
  name: string;
  scope: OmsPresetScope;
  report: OmsReport;
}) {
  const changes = presetChanges(saved, draft, name, scope);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  return (
    <SpaceBetween size="s">
      <Box variant="h3">Changes to “{saved.name}”</Box>
      {changes.summaries.length ? (
        <>
          <ul className="oms-report-download__applied-filters">
            {changes.summaries.map((summary) => (
              <li key={summary}>{summary}</li>
            ))}
          </ul>
          <ExpandableSection
            headerText={`Change details (${changes.details.length})`}
            expanded={detailsExpanded}
            onChange={({ detail }) => setDetailsExpanded(detail.expanded)}
          >
            {/* Keyboard users must be able to scroll the bounded detail region. */}
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
            {detailsExpanded && (
              <div
                className="oms-preset-changes"
                role="region"
                aria-label="Saved and updated preset settings"
                tabIndex={0}
              >
                <Table<PresetChange>
                  variant="embedded"
                  cellVerticalAlign="top"
                  stickyHeader
                  wrapLines
                  items={changes.details}
                  trackBy="id"
                  columnDefinitions={changeColumns(report, saved)}
                />
              </div>
            )}
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          </ExpandableSection>
        </>
      ) : (
        <Box color="text-body-secondary">No settings changed.</Box>
      )}
    </SpaceBetween>
  );
}
